import type { OptimizelyDatafile } from '#types/p13n'
import { createInstance } from '@optimizely/optimizely-sdk'

type OptimizelyClient = ReturnType<typeof createInstance>
declare module '#app' {
  interface NuxtApp {
    $optimizelyClient: OptimizelyClient
    $optimizelyDataReady: (userEventData: any) => void
  }
}

declare module '@vue/runtime-core' {
  interface ComponentCustomProperties {
    $optimizelyClient: OptimizelyClient
    $optimizelyDataReady: (userEventData: any) => void
  }
}

export default defineNuxtPlugin({
  name: 'optimizely',
  setup: async () => {
    const {
      configP13n: { optimizelySdkKey, enableOptimizely }
    } = useFeatureFlags()

    if (!enableOptimizely) return {}

    const datafileUrl = `https://cdn.optimizely.com/datafiles/${optimizelySdkKey}.json`

    const userId = useCookie('optimizelyUserId', {
      maxAge: 60 * 60 * 24 * 365
    })
    const optimizelyVisitNumber = useCookie('optimizelyVisitNumber', {
      default: () => 0,
      encode: (value) => String(value),
      decode: (value) => Number(value),
      maxAge: 60 * 60 * 24 * 90
    })

    const optimizelyActiveVisit = useCookie('optimizelyActiveVisit', {
      default: () => undefined,
      encode: (value) => String(value),
      decode: (value) => value === 'true',
    })

    const optimizelyLandingPage = useCookie('optimizelyLandingPage', {
      default: () => undefined,
      encode: (value) => String(value),
      decode: (value) => value === 'true',
    })

    if (isClient()) {
      if (!optimizelyActiveVisit.value) {
        optimizelyVisitNumber.value++
        optimizelyActiveVisit.value = true
        optimizelyLandingPage.value = true
      }
      else {
        optimizelyLandingPage.value = false
      }

      userId.value ??= uuid()
    }

    let optimizelyClient: OptimizelyClient | null = null

    try {
      const datafile = await $fetch(datafileUrl)
      optimizelyClient = createInstance({
        datafile: datafile as OptimizelyDatafile,
      })
    }
    catch (err) {
      console.warn('[Optimizely] Failed to fetch datafile:', err)
      optimizelyClient = createInstance({})
    }

    const optimizelyDataReady = (userEventData) => {
      if (!isClient()) return
      window.dispatchEvent(new Event('vfUserDataAvailable'))
      if (userEventData) {
        console.info('vfUserDataAvailable event fired')
      }
    }

    return {
      provide: {
        optimizelyClient,
        optimizelyDataReady
      }
    }
  }
})
